import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Loader2, LogOut, Mail, MessageCircle, Phone, Search, ShieldCheck, Sparkles, TrendingUp, Users, Video } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DuoClairLogo } from "@/components/DuoClairLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const STATUSES = ["Nuovo", "Da analizzare", "Contattato", "Proposta inviata", "Cliente", "Perso"];

export const STATUS_STYLES = {
  Nuovo: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "Da analizzare": "bg-amber-500/10 text-amber-400 border-amber-500/30",
  Contattato: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  "Proposta inviata": "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  Cliente: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Perso: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

const CONTACT_ICONS = { Telefono: Phone, Email: Mail, WhatsApp: MessageCircle };

export const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("Tutti");
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(async (status = "Tutti") => {
    try {
      const params = status !== "Tutti" ? { status } : {};
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data.leads);
      setStats(data.stats);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load("Tutti");
    api.get("/config").then((r) => setVideoUrl(r.data.video_url)).catch(() => {});
  }, [load]);

  const changeFilter = (s) => {
    setFilter(s);
    setLoading(true);
    load(s);
  };

  const changeStatus = async (leadId, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? data : l)));
      const { data: fresh } = await api.get("/admin/leads", { params: filter !== "Tutti" ? { status: filter } : {} });
      setStats(fresh.stats);
      toast.success(`Stato aggiornato: ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      await api.put("/admin/config/video", { video_url: videoUrl });
      toast.success("URL del video aggiornato sulla landing page");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingVideo(false);
    }
  };

  const changePassword = async () => {
    if (pwForm.next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password aggiornata con successo");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  const statCards = stats
    ? [
        { label: "Lead totali", value: stats.total, icon: Users },
        { label: "Nuovi", value: stats.per_status["Nuovo"] || 0, icon: Sparkles },
        { label: "Da analizzare", value: stats.per_status["Da analizzare"] || 0, icon: Search },
        { label: "Clienti", value: stats.per_status["Cliente"] || 0, icon: TrendingUp },
        { label: "Conversione", value: `${stats.conversion_rate}%`, icon: TrendingUp },
      ]
    : [];

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-obsidian/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <DuoClairLogo compact />
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-gold transition-colors" data-testid="admin-view-site-link">
              <ExternalLink className="w-3.5 h-3.5" /> Vedi il sito
            </Link>
            <span className="hidden md:block text-xs text-zinc-600 font-mono">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="text-zinc-400 hover:text-gold hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10 space-y-10">
        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="admin-stats">
          {statCards.map(({ label, value, icon: Icon }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="luxury-card p-4 sm:p-5"
              data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className="w-4 h-4 text-gold/70 mb-3" />
              <p className="font-serif text-2xl sm:text-3xl text-slate-100">{value}</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mt-1">{label}</p>
            </motion.div>
          ))}
        </section>

        {/* Filters */}
        <section>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
            {["Tutti", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => changeFilter(s)}
                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full border px-4 py-2 text-xs sm:text-sm transition-all duration-300 ${
                  filter === s
                    ? "border-gold/60 bg-gold/10 text-gold-light"
                    : "border-white/10 bg-white/[0.03] text-zinc-400 hover:border-white/25 hover:text-slate-200"
                }`}
              >
                {s}
                {stats && s !== "Tutti" && <span className="ml-2 font-mono text-[10px] opacity-70">{stats.per_status[s] || 0}</span>}
                {stats && s === "Tutti" && <span className="ml-2 font-mono text-[10px] opacity-70">{stats.total}</span>}
              </button>
            ))}
          </div>

          {/* Leads */}
          {loading ? (
            <p className="text-zinc-500 text-sm py-10 text-center" data-testid="leads-loading">Caricamento lead…</p>
          ) : leads.length === 0 ? (
            <div className="luxury-card p-12 text-center" data-testid="leads-empty">
              <p className="font-serif text-xl text-zinc-400 italic">Nessun lead in questo stato.</p>
              <p className="text-sm text-zinc-600 mt-2">I nuovi contatti dalla landing page appariranno qui.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="leads-list">
              {leads.map((lead, i) => {
                const ContactIcon = CONTACT_ICONS[lead.preferred_contact_method] || Phone;
                return (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                    className="luxury-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-gold/25 transition-colors duration-300"
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <Link to={`/admin/leads/${lead.id}`} className="flex-1 min-w-0 group" data-testid={`lead-open-${lead.id}`}>
                      <div className="flex items-center gap-3 mb-1.5">
                        <h3 className="font-serif text-lg sm:text-xl text-slate-100 truncate group-hover:text-gold-light transition-colors">
                          {lead.business_name}
                        </h3>
                        <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider border rounded-full px-2.5 py-0.5 ${STATUS_STYLES[lead.status]}`} data-testid={`lead-status-badge-${lead.id}`}>
                          {lead.status}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-zinc-500 truncate">
                        {lead.industry} · {lead.city} · {lead.main_objective} · {fmtDate(lead.created_at)} · Fonte: {lead.source}
                      </p>
                    </Link>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <ContactIcon className="w-3.5 h-3.5 text-gold/70" />
                        <span className="max-w-[140px] truncate">{lead.contact_info}</span>
                      </span>
                      <Select value={lead.status} onValueChange={(v) => changeStatus(lead.id, v)}>
                        <SelectTrigger className="w-[150px] h-9 bg-white/[0.04] border-white/10 text-xs rounded-lg" data-testid={`lead-status-select-${lead.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-coal border-white/10">
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="text-xs text-slate-200 focus:bg-gold/10 focus:text-gold-light" data-testid={`status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

        {/* Video settings */}
        <section className="luxury-card p-6 sm:p-8" data-testid="video-settings">
          <div className="flex items-center gap-3 mb-4">
            <Video className="w-5 h-5 text-gold" />
            <h2 className="font-serif text-xl sm:text-2xl text-slate-100">Video della landing page</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-5">
            Incolla qui l'URL del tuo video di presentazione (mp4). La landing page si aggiorna immediatamente.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://…/presentazione.mp4"
              className="input-lux flex-1"
              data-testid="video-url-input"
            />
            <Button
              onClick={saveVideo}
              disabled={savingVideo || !videoUrl.trim()}
              data-testid="video-url-save-button"
              className="h-12 px-6 rounded-xl bg-gradient-to-r from-gold-dark via-gold to-gold-light text-black font-semibold hover:opacity-95 disabled:opacity-40"
            >
              {savingVideo ? "Salvataggio…" : "Salva URL"}
            </Button>
          </div>
        </section>
        {/* Security — change password */}
        <section className="luxury-card p-6 sm:p-8" data-testid="security-settings">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-gold" />
            <h2 className="font-sans font-bold text-xl sm:text-2xl text-slate-100">Sicurezza account</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-5">
            Cambia la password di accesso all'area riservata. La password non viene mai mostrata.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Input
              type="password"
              autoComplete="current-password"
              value={pwForm.current}
              onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
              placeholder="Password attuale"
              className="input-lux"
              data-testid="pw-current-input"
            />
            <Input
              type="password"
              autoComplete="new-password"
              value={pwForm.next}
              onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
              placeholder="Nuova password (min. 8)"
              className="input-lux"
              data-testid="pw-new-input"
            />
            <Input
              type="password"
              autoComplete="new-password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
              placeholder="Conferma nuova password"
              className="input-lux"
              data-testid="pw-confirm-input"
            />
          </div>
          <Button
            onClick={changePassword}
            disabled={pwSaving || !pwForm.current || !pwForm.next}
            data-testid="pw-save-button"
            className="h-11 px-6 rounded-xl bg-gradient-to-b from-gold-light via-gold to-gold-dark text-black font-semibold hover:opacity-95 disabled:opacity-40"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna password"}
          </Button>
        </section>
      </main>
    </div>
  );
}
